import { type Incident, IncidentStatus } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaAngleDown } from 'react-icons/fa';
import { FormStack } from '../../components/forms/FormStack';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedTextArea } from '../../components/forms/HookedTextArea';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal, showModal } from '../../components/generic/Modal';
import { useUpdateIncidentStatusMutation } from '../../graphql/hooks/useUpdateIncidentStatusMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { usePermission } from '../../hooks/usePermission';
import { incidentStatusColorName } from './IncidentStatusTag';

interface FormValues {
  actions: string;
}

interface Props {
  incident: Incident;
}

const ReviewedModal = createModal<Props>((props) => {
  const { incident, modal } = props;
  const { organization, successToast, errorToast } = useAppContext();
  const { hide, remove, visible } = modal;

  const methods = useForm<FormValues>({
    defaultValues: {
      actions: incident.actions ?? undefined,
    },
  });

  const mutation = useUpdateIncidentStatusMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          incidentId: incident.id,
          request: {
            status: IncidentStatus.Reviewed,
            actions: values.actions,
          },
        })
        .then(() => {
          successToast('Incident marked as reviewed');
          hide();
        })
        .catch(() => {
          errorToast('Error updating incident status');
        });
    },
    [mutation, organization.id, incident.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle title='Do you want to mark the incident as reviewed?' />
              <ChakraV3.Dialog.Body>
                <FormStack>
                  <HookedFormField
                    name='actions'
                    label='Actions to be taken'
                    helperText='Describe what actions will be taken, and who is responsible, to prevent recurrence'
                  >
                    <HookedTextArea
                      name='actions'
                      placeholder='e.g. "Incident report to be reviewed by manager"'
                    />
                  </HookedFormField>
                </FormStack>
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button size='sm' onClick={hide} variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton size='sm'>Review</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});

const AwaitingReviewModal = createModal<Props>((props) => {
  const { incident, modal } = props;
  const { organization, successToast, errorToast } = useAppContext();
  const { hide, remove, visible } = modal;

  const mutation = useUpdateIncidentStatusMutation();

  const onClick = React.useCallback(() => {
    mutation
      .mutateAsync({
        organizationId: organization.id,
        incidentId: incident.id,
        request: {
          status: IncidentStatus.AwaitingReview,
        },
      })
      .then(() => {
        successToast('Incident marked as awaiting review');
        hide();
      })
      .catch(() => {
        errorToast('Error updating incident status');
      });
  }, [mutation, organization.id, incident.id, hide, successToast, errorToast]);

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle title='Do you want to mark the incident as awaiting review?' />
          <ChakraV3.Dialog.Body>
            <ChakraV3.Stack gap={0}>Are you sure you want to continue?</ChakraV3.Stack>
          </ChakraV3.Dialog.Body>
          <ChakraV3.Dialog.Footer>
            <ChakraV3.HStack>
              <ChakraV3.Button size='sm' onClick={hide} variant='subtle'>
                Close
              </ChakraV3.Button>
              <ChakraV3.Button
                size='sm'
                onClick={onClick}
                loading={mutation.isLoading}
                colorPalette='brand'
              >
                OK
              </ChakraV3.Button>
            </ChakraV3.HStack>
          </ChakraV3.Dialog.Footer>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});

const AwaitingReviewMenuItemOption = (props: Props) => {
  const { incident } = props;
  const hasPermission = true;

  const isChecked = incident.status === IncidentStatus.AwaitingReview;
  const isDisabled = !hasPermission || isChecked;

  return (
    <ChakraV3.MenuRadioItem
      onClick={() => showModal(AwaitingReviewModal, { incident })}
      disabled={isDisabled}
      value={IncidentStatus.AwaitingReview}
    >
      Awaiting review
      <ChakraV3.Menu.ItemIndicator />
    </ChakraV3.MenuRadioItem>
  );
};

const ReviewedMenuItemOption = (props: Props) => {
  const { incident } = props;
  const hasPermission = true;

  const isChecked = incident.status === IncidentStatus.Reviewed;
  const isDisabled = !hasPermission || isChecked;

  return (
    <ChakraV3.MenuRadioItem
      onClick={() => showModal(ReviewedModal, { incident })}
      disabled={isDisabled}
      value={IncidentStatus.Reviewed}
    >
      Reviewed
      <ChakraV3.Menu.ItemIndicator />
    </ChakraV3.MenuRadioItem>
  );
};

export const IncidentUpdateStatusControl = (props: Props) => {
  const { incident } = props;

  const [_color, name] = incidentStatusColorName(incident.status);

  const hasPermission = usePermission('review', 'incident');

  return (
    <ChakraV3.Menu.Root>
      <ChakraV3.Menu.Trigger>
        <ChakraV3.Button disabled={!hasPermission} colorPalette='brand' size='sm'>
          {name}
          <FaAngleDown />
        </ChakraV3.Button>
      </ChakraV3.Menu.Trigger>
      <ChakraV3.Menu.Positioner>
        <ChakraV3.Menu.Content>
          <ChakraV3.Menu.RadioItemGroup value={incident.status}>
            <AwaitingReviewMenuItemOption incident={incident} />
            <ReviewedMenuItemOption incident={incident} />
          </ChakraV3.Menu.RadioItemGroup>
        </ChakraV3.Menu.Content>
      </ChakraV3.Menu.Positioner>
    </ChakraV3.Menu.Root>
  );
};
